import React from "react";
import moment from "moment";
import {
  TextInput,
  Switch,
  Badge,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconTrash, IconList } from "@tabler/icons";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import ApprovalFundListFileImporter from "../../components/approvalFundListFileImporter";
import ApprovalFundListFunds from "../../components/approvalFundListFunds";
import ApprovalFundAcknowledgementList from "../../components/approvalFundAcknowledgementList";

const apiEntity = "approvalFundList";

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "";

const formatEffectivePeriod = (data) => {
  const from = data.effectiveFrom ?? data.date;
  const to = data.effectiveTo ?? null;
  if (!from && !to) return "--";
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  return formatDate(from || to);
};

const ReadOnlyQuizFlag = ({ form }) => (
  <TextInput
    label="Need Quiz Acknowledge"
    value={form.values.needQuizAcknowledge !== false ? "Yes" : "No"}
    readOnly
  />
);

const validateEffectiveTo = (value, values) => {
  if (!value) return null;
  const from = values.effectiveFrom;
  if (from && moment(value).isBefore(moment(from), "day")) {
    return "Effective To must be on or after Effective From";
  }
  return null;
};

const addFormSchema = {
  title: "Add Approval Fund List",
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "lg",
  },
  apiEntity,
  initialValues: {
    effectiveFrom: new Date(),
    effectiveTo: null,
    needQuizAcknowledge: true,
    funds: [],
    fileName: "",
    acknowledgements: [],
  },
  validate: {
    effectiveFrom: (value) => (value ? null : "Effective From is required"),
    effectiveTo: validateEffectiveTo,
    funds: (value) =>
      Array.isArray(value) && value.length > 0
        ? null
        : "Please upload a fund list file with at least one fund",
  },
  layout: {
    fields: [
      {
        name: "effectiveFrom",
        component: DatePicker,
        type: "datePicker",
        props: {
          label: "Effective From",
          required: true,
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "effectiveTo",
        component: DatePicker,
        type: "datePicker",
        props: {
          label: "Effective To",
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "needQuizAcknowledge",
        component: Switch,
        props: {
          label: "Need Quiz Acknowledge",
          type: "checkbox",
        },
      },
      {
        name: "funds",
        component: ApprovalFundListFileImporter,
        props: {
          label: "Fund List File",
          required: true,
          withAsterisk: true,
        },
      },
    ],
  },
};

const editFormSchema = {
  title: "Approval Fund List",
  titleField: "code",
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
    size: "xl",
  },
  apiEntity,
  initialValues: {
    acknowledgements: [],
    funds: [],
  },
  layout: {
    containers: [
      {
        key: "main-tab",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default",
          defaultValue: "Fund Table",
        },
        tab: [
          { key: "tab-funds", label: "Fund Table", icon: IconList },
          { key: "tab-ack", label: "Acknowledgement", icon: IconList },
        ],
      },
      {
        key: "basic-section",
        parent: "tab-funds",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Basic Info",
          description: "Approval fund list details",
        },
      },
      {
        key: "basic-grid",
        parent: "basic-section",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          { key: "basic-g1", props: { span: 6 } },
          { key: "basic-g2", props: { span: 6 } },
        ],
      },
      {
        key: "funds-section",
        parent: "tab-funds",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Funds",
          description: "Funds imported from the uploaded file",
        },
      },
      {
        key: "ack-section",
        parent: "tab-ack",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Acknowledgement",
          description: "Users who have passed the quiz for this list",
        },
      },
    ],
    fields: [
      {
        name: "effectiveFrom",
        parent: "basic-g1",
        component: DatePicker,
        type: "datePicker",
        props: {
          label: "Effective From",
          readOnly: true,
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "effectiveTo",
        parent: "basic-g1",
        component: DatePicker,
        type: "datePicker",
        props: {
          label: "Effective To",
          readOnly: true,
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "needQuizAcknowledge",
        parent: "basic-g2",
        component: ReadOnlyQuizFlag,
        props: {},
      },
      {
        name: "funds",
        parent: "funds-section",
        component: ApprovalFundListFunds,
        props: {},
      },
      {
        name: "acknowledgements",
        parent: "ack-section",
        component: ApprovalFundAcknowledgementList,
        props: {},
      },
    ],
  },
};

const ApprovalFundList = () => {
  const tableSchema = {
    showActionColumn: true,
    columns: [
      {
        field: "code",
        headerName: "Code",
        sortable: true,
      },
      {
        field: "effectiveFrom",
        headerName: "Effective Period",
        sortable: true,
        cellRender: (_col, data) => formatEffectivePeriod(data),
      },
      {
        field: "needQuizAcknowledge",
        headerName: "Need Quiz",
        sortable: true,
        cellRender: (col, data) => (
          <Badge color={data.needQuizAcknowledge !== false ? "blue" : "gray"}>
            {data.needQuizAcknowledge !== false ? "Yes" : "No"}
          </Badge>
        ),
      },
      {
        field: "funds",
        headerName: "Number of Funds",
        sortable: false,
        cellRender: (_col, data) => data.funds?.length ?? 0,
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
          },
        ],
        actionMenu: [
          {
            name: "delete",
            label: "Delete data",
            icon: IconTrash,
            props: { color: "red" },
          },
        ],
      },
    ],
    searchableFields: ["code"],
  };

  return (
    <PageList
      title="Approval Fund List"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={{
        add: addFormSchema,
        edit: editFormSchema,
      }}
      initSort={{ by: "effectiveFrom", order: "desc" }}
    />
  );
};

export default ApprovalFundList;
